import React, { useState, useEffect } from 'react';
import { X, Filter, Users, DollarSign, RotateCcw, Truck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface SimpleFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onFiltersApply: (filters: FilterState) => void;
  availableDrivers: string[];
  availableEquipmentTypes: string[];
  currentFilters: FilterState;
}

export interface FilterState {
  excludedDrivers: string[];
  selectedEquipmentTypes: string[]; // Equipment types to include (checked = show)
  rpmExclusionEnabled: boolean;
  rpmExclusionRange: [number, number]; // [min, max] - loads outside this range will be excluded
  rpmMinEnabled: boolean; // Enable minimum RPM filter
  rpmMaxEnabled: boolean; // Enable maximum RPM filter
}

export const defaultFilters: FilterState = {
  excludedDrivers: [],
  selectedEquipmentTypes: [], // Empty = show none (will be populated with all types on init)
  rpmExclusionEnabled: false,
  rpmExclusionRange: [0.80, 40.00],
  rpmMinEnabled: false,
  rpmMaxEnabled: false,
};

export default function SimpleFilters({ 
  isOpen, 
  onClose, 
  onFiltersApply, 
  availableDrivers,
  availableEquipmentTypes,
  currentFilters 
}: SimpleFiltersProps) {
  const [filters, setFilters] = useState<FilterState>(currentFilters);

  // Update local state when currentFilters changes
  useEffect(() => {
    setFilters(currentFilters);
  }, [currentFilters]);

  // Reset filters to default
  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  // Apply filters and close modal
  const handleApply = () => {
    onFiltersApply(filters);
    onClose();
  };

  // Toggle driver exclusion
  const toggleDriverExclusion = (driver: string) => {
    setFilters(prev => ({
      ...prev,
      excludedDrivers: prev.excludedDrivers.includes(driver)
        ? prev.excludedDrivers.filter(d => d !== driver)
        : [...prev.excludedDrivers, driver]
    }));
  };

  // Toggle equipment type selection
  const toggleEquipmentType = (equipmentType: string) => {
    setFilters(prev => {
      // Normal toggle behavior
      if (prev.selectedEquipmentTypes.includes(equipmentType)) {
        return {
          ...prev,
          selectedEquipmentTypes: prev.selectedEquipmentTypes.filter(e => e !== equipmentType)
        };
      } else {
        return {
          ...prev,
          selectedEquipmentTypes: [...prev.selectedEquipmentTypes, equipmentType]
        };
      }
    });
  };

  // Count active filters
  const rpmFiltersActive = (filters.rpmMinEnabled ? 1 : 0) + (filters.rpmMaxEnabled ? 1 : 0);
  const equipmentFilterActive = filters.selectedEquipmentTypes.length > 0 && filters.selectedEquipmentTypes.length < availableEquipmentTypes.length ? 1 : 0;
  const activeFilterCount = filters.excludedDrivers.length + equipmentFilterActive + rpmFiltersActive;

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-netflix-grey-800 border border-netflix-grey-600 rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-netflix-grey-600">
            <div className="flex items-center space-x-3">
              <div className="bg-netflix-red/20 p-2 rounded-lg">
                <Filter className="w-5 h-5 text-netflix-red" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Filters</h2>
                <p className="text-sm text-netflix-grey-400">
                  {activeFilterCount > 0 ? `${activeFilterCount} filter${activeFilterCount > 1 ? 's' : ''} active` : 'No active filters'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-netflix-grey-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto space-y-6">
            
            {/* Driver Exclusion Section */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Users className="w-5 h-5 text-netflix-grey-300" />
                <h3 className="text-lg font-semibold text-white">Exclude Drivers</h3>
                <span className="text-sm text-netflix-grey-400">
                  ({filters.excludedDrivers.length} excluded)
                </span>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableDrivers.length > 0 ? (
                  availableDrivers.map((driver) => (
                    <label key={driver} className="flex items-center p-2 hover:bg-netflix-grey-700/50 rounded-lg cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.excludedDrivers.includes(driver)}
                        onChange={() => toggleDriverExclusion(driver)}
                        className="w-4 h-4 text-netflix-red bg-netflix-grey-700 border-netflix-grey-600 rounded focus:ring-netflix-red focus:ring-2"
                      />
                      <span className="ml-3 text-netflix-grey-300">{driver}</span>
                    </label>
                  ))
                ) : (
                  <div className="text-center py-4 text-netflix-grey-400">
                    No drivers available
                  </div>
                )}
              </div>

              {/* Driver exclusion controls */}
              {availableDrivers.length > 0 && (
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, excludedDrivers: [] }))}
                    className="text-sm px-3 py-1 text-netflix-grey-400 hover:text-white transition-colors"
                  >
                    Clear All
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, excludedDrivers: [...availableDrivers] }))}
                    className="text-sm px-3 py-1 text-netflix-grey-400 hover:text-white transition-colors"
                  >
                    Exclude All
                  </button>
                </div>
              )}
            </div>

            {/* RPM Range Filter Section */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="w-5 h-5 text-netflix-grey-300" />
                <h3 className="text-lg font-semibold text-white">RPM Range Filter</h3>
                <span className="text-sm text-netflix-grey-400">
                  ({rpmFiltersActive > 0 ? `${rpmFiltersActive} bound${rpmFiltersActive > 1 ? 's' : ''} active` : 'disabled'})
                </span>
              </div>

              <div className="space-y-4">
                {/* Minimum RPM Filter */}
                <div className="border border-netflix-grey-600 rounded-lg p-4">
                  <label className="flex items-center mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.rpmMinEnabled}
                      onChange={(e) => setFilters(prev => ({ 
                        ...prev, 
                        rpmMinEnabled: e.target.checked,
                        rpmExclusionEnabled: e.target.checked || prev.rpmMaxEnabled
                      }))}
                      className="w-4 h-4 text-netflix-red bg-netflix-grey-700 border-netflix-grey-600 rounded focus:ring-netflix-red focus:ring-2"
                    />
                    <span className="ml-3 font-medium text-netflix-grey-200">
                      Minimum RPM Filter
                      {filters.rpmMinEnabled && (
                        <span className="ml-2 text-netflix-grey-400">
                          (RPM ≥ ${filters.rpmExclusionRange[0].toFixed(2)})
                        </span>
                      )}
                    </span>
                  </label>
                  
                  {filters.rpmMinEnabled && (
                    <div>
                      <label className="block text-sm text-netflix-grey-400 mb-2">
                        Show loads with RPM at least: ${filters.rpmExclusionRange[0].toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="40"
                        step="0.10"
                        value={filters.rpmExclusionRange[0]}
                        onChange={(e) => {
                          const newMin = parseFloat(e.target.value);
                          setFilters(prev => ({
                            ...prev,
                            rpmExclusionRange: [newMin, prev.rpmExclusionRange[1]]
                          }));
                        }}
                        className="w-full h-2 bg-netflix-grey-700 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-netflix-grey-500 mt-1">
                        <span>$0.00</span>
                        <span>$40.00</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Maximum RPM Filter */}
                <div className="border border-netflix-grey-600 rounded-lg p-4">
                  <label className="flex items-center mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.rpmMaxEnabled}
                      onChange={(e) => setFilters(prev => ({ 
                        ...prev, 
                        rpmMaxEnabled: e.target.checked,
                        rpmExclusionEnabled: prev.rpmMinEnabled || e.target.checked
                      }))}
                      className="w-4 h-4 text-netflix-red bg-netflix-grey-700 border-netflix-grey-600 rounded focus:ring-netflix-red focus:ring-2"
                    />
                    <span className="ml-3 font-medium text-netflix-grey-200">
                      Maximum RPM Filter
                      {filters.rpmMaxEnabled && (
                        <span className="ml-2 text-netflix-grey-400">
                          (RPM ≤ ${filters.rpmExclusionRange[1].toFixed(2)})
                        </span>
                      )}
                    </span>
                  </label>
                  
                  {filters.rpmMaxEnabled && (
                    <div>
                      <label className="block text-sm text-netflix-grey-400 mb-2">
                        Show loads with RPM at most: ${filters.rpmExclusionRange[1].toFixed(2)}
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="40"
                        step="0.10"
                        value={filters.rpmExclusionRange[1]}
                        onChange={(e) => {
                          const newMax = parseFloat(e.target.value);
                          setFilters(prev => ({
                            ...prev,
                            rpmExclusionRange: [prev.rpmExclusionRange[0], newMax]
                          }));
                        }}
                        className="w-full h-2 bg-netflix-grey-700 rounded-lg appearance-none cursor-pointer slider"
                      />
                      <div className="flex justify-between text-xs text-netflix-grey-500 mt-1">
                        <span>$0.00</span>
                        <span>$40.00</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Quick presets - only show if either bound is enabled */}
                {(filters.rpmMinEnabled || filters.rpmMaxEnabled) && (
                  <div>
                    <label className="block text-sm text-netflix-grey-400 mb-2">Quick Presets:</label>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <button
                        onClick={() => setFilters(prev => ({ 
                          ...prev, 
                          rpmExclusionRange: [2.00, 5.00],
                          rpmMinEnabled: true,
                          rpmMaxEnabled: true,
                          rpmExclusionEnabled: true
                        }))}
                        className="px-3 py-1 bg-netflix-grey-700 text-netflix-grey-300 rounded hover:bg-netflix-grey-600 transition-colors"
                      >
                        Standard Range ($2.00-$5.00)
                      </button>
                      <button
                        onClick={() => setFilters(prev => ({ 
                          ...prev, 
                          rpmExclusionRange: [10.00, prev.rpmExclusionRange[1]],
                          rpmMinEnabled: true,
                          rpmMaxEnabled: false,
                          rpmExclusionEnabled: true
                        }))}
                        className="px-3 py-1 bg-netflix-grey-700 text-netflix-grey-300 rounded hover:bg-netflix-grey-600 transition-colors"
                      >
                        Premium Only ($10+)
                      </button>
                      <button
                        onClick={() => setFilters(prev => ({ 
                          ...prev, 
                          rpmExclusionRange: [5.00, prev.rpmExclusionRange[1]],
                          rpmMinEnabled: true,
                          rpmMaxEnabled: false,
                          rpmExclusionEnabled: true
                        }))}
                        className="px-3 py-1 bg-netflix-grey-700 text-netflix-grey-300 rounded hover:bg-netflix-grey-600 transition-colors"
                      >
                        High Performance ($5+)
                      </button>
                      <button
                        onClick={() => setFilters(prev => ({ 
                          ...prev, 
                          rpmExclusionRange: [prev.rpmExclusionRange[0], 5.00],
                          rpmMinEnabled: false,
                          rpmMaxEnabled: true,
                          rpmExclusionEnabled: true
                        }))}
                        className="px-3 py-1 bg-netflix-grey-700 text-netflix-grey-300 rounded hover:bg-netflix-grey-600 transition-colors"
                      >
                        Filter Outliers (Max $5)
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Equipment Type Filter Section */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Truck className="w-5 h-5 text-netflix-grey-300" />
                <h3 className="text-lg font-semibold text-white">Equipment Type Filter</h3>
                <span className="text-sm text-netflix-grey-400">
                  ({filters.selectedEquipmentTypes.length === 0 ? 'none selected' : `${filters.selectedEquipmentTypes.length} selected`})
                </span>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableEquipmentTypes.length > 0 ? (
                  availableEquipmentTypes.map((equipmentType) => {
                    // Checkbox is checked if the equipment type is in the selected list
                    const isChecked = filters.selectedEquipmentTypes.includes(equipmentType);

                    return (
                      <label key={equipmentType} className="flex items-center p-2 hover:bg-netflix-grey-700/50 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleEquipmentType(equipmentType)}
                          className="w-4 h-4 text-netflix-red bg-netflix-grey-700 border-netflix-grey-600 rounded focus:ring-netflix-red focus:ring-2"
                        />
                        <span className="ml-3 text-netflix-grey-300">{equipmentType}</span>
                      </label>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-netflix-grey-400">
                    No equipment types available
                  </div>
                )}
              </div>

              {/* Equipment type controls */}
              {availableEquipmentTypes.length > 0 && (
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, selectedEquipmentTypes: [...availableEquipmentTypes] }))}
                    className="text-sm px-3 py-1 text-netflix-grey-400 hover:text-white transition-colors"
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setFilters(prev => ({ ...prev, selectedEquipmentTypes: [] }))}
                    className="text-sm px-3 py-1 text-netflix-grey-400 hover:text-white transition-colors"
                  >
                    Deselect All
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-netflix-grey-600">
            <button
              onClick={resetFilters}
              className="flex items-center space-x-2 text-netflix-grey-400 hover:text-white transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-netflix-grey-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApply}
                className="btn-primary flex items-center space-x-2"
              >
                <Filter className="w-4 h-4" />
                <span>Apply Filters</span>
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}